import { AxiosResponse } from 'axios'
import {
  QueryKey,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from 'react-query'
import { toast } from 'react-toastify'

import { handleException } from 'clients/exception-handling'
import { HomeService } from 'features/projects/service'
import { BulkUpdatePayload } from 'features/projects/service/service'
import { Project } from 'features/projects/types'

type ProjectsResponse = AxiosResponse<Project[]>

type MutationContext = {
  snapshots: Array<[QueryKey, ProjectsResponse | undefined]>
}

export const useBulkUpdateProjects = (
  service: HomeService,
): UseMutationResult<
  { updated: number },
  unknown,
  BulkUpdatePayload,
  MutationContext
> => {
  const queryClient = useQueryClient()
  return useMutation<
    { updated: number },
    unknown,
    BulkUpdatePayload,
    MutationContext
  >({
    meta: { operation: 'projects.bulk-update' },
    mutationFn: (payload: BulkUpdatePayload) =>
      service.bulkUpdateProjects(payload).then((r) => r.data),
    // Optimistic update: patch the cache locally so the UI flips instantly,
    // and skip the post-success invalidate (which previously refetched all
    // ~1.2k projects on every toggle, causing visible lag).
    onMutate: async (payload) => {
      await queryClient.cancelQueries(['home', 'projects'])

      const ids = new Set(payload.proj_uuids)
      const snapshots = queryClient.getQueriesData<ProjectsResponse>([
        'home',
        'projects',
      ])

      queryClient.setQueriesData<ProjectsResponse | undefined>(
        ['home', 'projects'],
        (prev) => {
          if (!prev?.data) return prev
          return {
            ...prev,
            data: prev.data.map((p) =>
              ids.has(p.uuid) ? { ...p, ...payload.body } : p,
            ),
          }
        },
      )

      return { snapshots }
    },
    onError: (...args) => {
      handleException(args[0], {
        operation: 'projects.bulk-update',
        disposition: 'delegated',
        owner: 'MutationCache',
      })

      const ctx = args[2]
      ctx?.snapshots.forEach(([key, value]) => {
        queryClient.setQueryData(key, value)
      })
      toast.error('Update failed — please try again')
    },
    onSuccess: (_, vars) => {
      const n = vars.proj_uuids.length
      toast.success(n === 1 ? 'Hub updated' : `Updated ${n} hubs`)
    },
  })
}
